import { Component, type ErrorInfo, type ReactNode } from 'react'
import { textoDoRelatorio } from '../erro/relatorioDeErro'

interface Props {
  children: ReactNode
}

interface Estado {
  // Guardo as PECAS, e nao o texto pronto: a pilha de componentes so chega no
  // `componentDidCatch`, que roda DEPOIS do `getDerivedStateFromError`. Montar o
  // texto no render deixa os dois momentos convergirem sem estado duplicado.
  erro: { mensagem: string; pilha: string | null } | null
  componentes: string | null
}

// O LIMITE DE ERRO do app. Nasceu de um defeito relatado em 18/08/2026: ao
// digitar a segunda letra do nome de um produto, a tela ficava BRANCA. Medido:
// o `#root` ia de 149 elementos para ZERO e o texto da tela virava string vazia.
// Um erro de render em qualquer canto derrubava o app inteiro, e o que chegava
// ate quem estava usando era uma tela branca MUDA -- sem mensagem, sem saida, e
// sem nada que eu pudesse usar para consertar.
//
// ⚠ Ele NAO conserta o defeito que causou o erro; ele faz o app dizer qual foi.
// Com 5 representantes na rua, essa diferenca e a diferenca entre "quebrou
// alguma coisa" e uma linha que aponta o arquivo.
//
// Class component de proposito: `getDerivedStateFromError` e `componentDidCatch`
// nao tem equivalente em hook -- o React nao oferece limite de erro funcional.
export default class LimiteDeErro extends Component<Props, Estado> {
  state: Estado = { erro: null, componentes: null }

  static getDerivedStateFromError(erro: unknown): Partial<Estado> {
    return {
      erro: {
        mensagem: erro instanceof Error ? erro.message : String(erro),
        pilha: erro instanceof Error ? (erro.stack ?? null) : null,
      },
    }
  }

  componentDidCatch(erro: unknown, info: ErrorInfo) {
    this.setState({ componentes: info.componentStack ?? null })
    // Tambem no console: quando o defeito acontece com o app aberto no
    // computador, o rastro completo vale mais que o resumo da tela.
    console.error('[LimiteDeErro]', erro, info.componentStack)
  }

  render() {
    const { erro, componentes } = this.state
    if (erro === null) return this.props.children

    const relatorio = textoDoRelatorio({
      ...erro,
      versao: __VERSAO_APP__,
      // `location` direto, e nao useLocation: o hook exige estar dentro do
      // Router, e este limite precisa valer inclusive quando o proprio Router
      // for o que quebrou.
      endereco: window.location.pathname,
      componentes,
    })

    return (
      <section className="p-4 space-y-4">
        <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
          <h1 className="text-lg font-bold text-slate-800">Algo quebrou nesta tela</h1>
          <p className="text-sm text-slate-600">
            O erro está escrito abaixo. Nada do que você já salvou foi perdido — o que estava no
            aparelho continua lá.
          </p>
          {/* `pre` com quebra de linha forcada: a mensagem de erro vem em uma
              linha longa e sem isto ela sai da tela num celular de 360px, que e
              onde este texto vai ser lido. */}
          <pre
            data-testid="detalhe-do-erro"
            className="text-xs text-slate-700 bg-slate-100 rounded-lg p-3 whitespace-pre-wrap break-words"
          >
            {relatorio}
          </pre>
          <p className="text-sm text-slate-600">
            Tire uma foto desta tela e mande para quem cuida do app: é por esse texto que o defeito
            se acha.
          </p>
          <div className="flex flex-wrap gap-2">
            {/* Recarga de verdade, e nao navegacao do React: a arvore quebrada
                nao volta ao normal sozinha, entao o caminho honesto e comecar
                de novo. */}
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="bg-teal-700 text-white rounded-lg px-4 py-2 text-sm"
            >
              Recarregar o app
            </button>
            <button
              type="button"
              onClick={() => window.location.assign('/')}
              className="border border-slate-300 text-slate-700 rounded-lg px-4 py-2 text-sm"
            >
              Ir para o Início
            </button>
          </div>
        </div>
      </section>
    )
  }
}
